import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { BadgeCheck, Building2, ExternalLink, FileText, RefreshCw, XCircle } from 'lucide-react';
import Screen from '../Layouts/Screen/Screen';
import { api } from '../../services/api';
import { errorMessage } from '../../utils/TextManagerTwo';
import { useAuth, useMessage } from '../../global-components/src';

interface CompanyDocument {
    label: string;
    url: string | null;
}

interface CompanySealRequest {
    id: number;
    corporate_reason: string;
    business_name: string;
    cnpj: string;
    website: string;
    email: string;
    created_at: string;
    documents: CompanyDocument[];
    user: {
        id: number;
        name: string;
        user_name: string;
        email: string;
        next_payment: string | null;
    } | null;
}

const formatDate = (value: string | null) => {
    if (!value) return '—';

    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return '—';

    return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(date);
};

const CompanySealRequests = () => {
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const [requests, setRequests] = useState<CompanySealRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState<number | null>(null);

    const loadRequests = useCallback(async () => {
        setLoading(true);

        try {
            const response = await api(token).get('/companies/verified/requests');
            setRequests(response.data.requests ?? []);
        } catch (error: any) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setLoading(false);
        }
    }, [setMessage, token]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const reviewRequest = async (request: CompanySealRequest, action: 'approve' | 'reject') => {
        if (action === 'approve' && !window.confirm(`Aprovar o selo black para ${request.business_name}?`)) {
            return;
        }

        let reason: string | null = null;
        if (action === 'reject') {
            reason = window.prompt(`Motivo da recusa para ${request.business_name} (opcional):`);
            if (reason === null) return;
        }

        setProcessingId(request.id);

        try {
            const response = await api(token).post(
                `/companies/verified/requests/${request.id}/${action}`,
                action === 'reject' ? { reason: (reason ?? '').trim() || undefined } : {},
            );

            setRequests(current => current.filter(item => item.id !== request.id));
            setMessage(response.data.message, 'success');
        } catch (error: any) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <Screen paddingTopTitle title="Solicitações de selo black">
            <Helmet>
                <title>Paçoca - Solicitações de selo black</title>
            </Helmet>

            <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                <p className="mb-0 opacity-75">
                    Analise os dados e documentos antes de aprovar ou recusar a empresa.
                </p>
                <button
                    type="button"
                    className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                    onClick={loadRequests}
                    disabled={loading}
                >
                    <RefreshCw size={17} className={loading ? 'spinner-border spinner-border-sm' : ''} />
                    Atualizar
                </button>
            </div>

            {loading && (
                <div className="text-center py-5">Carregando solicitações…</div>
            )}

            {!loading && requests.length === 0 && (
                <div className="text-center py-5 opacity-75">
                    <BadgeCheck size={44} className="mb-3" />
                    <h5>Nenhuma solicitação pendente</h5>
                </div>
            )}

            {!loading && requests.map(request => {
                const isProcessing = processingId === request.id;

                return (
                    <article key={request.id} className="card mb-3 shadow-sm" style={{ background: 'var(--color-bg-post)' }}>
                        <div className="card-body">
                            <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <Building2 size={21} />
                                        <h5 className="mb-0">{request.business_name}</h5>
                                    </div>
                                    <div className="opacity-75">{request.corporate_reason}</div>
                                </div>
                                <small className="opacity-75">Solicitada em {formatDate(request.created_at)}</small>
                            </div>

                            <dl className="row mb-3">
                                <dt className="col-sm-3">CNPJ</dt>
                                <dd className="col-sm-9">{request.cnpj}</dd>

                                <dt className="col-sm-3">Site</dt>
                                <dd className="col-sm-9 text-break">
                                    <a href={request.website} target="_blank" rel="noreferrer">{request.website}</a>
                                </dd>

                                <dt className="col-sm-3">E-mail empresarial</dt>
                                <dd className="col-sm-9 text-break">{request.email}</dd>

                                <dt className="col-sm-3">Conta Paçoca</dt>
                                <dd className="col-sm-9">
                                    {request.user ? (
                                        <a href={`/@${request.user.user_name}`}>@{request.user.user_name} · {request.user.name}</a>
                                    ) : 'Conta indisponível'}
                                </dd>
                            </dl>

                            <div className="mb-4">
                                <strong className="d-block mb-2">Documentos enviados</strong>
                                <div className="d-flex flex-wrap gap-2">
                                    {request.documents.map(document => (
                                        document.url ? (
                                            <a
                                                key={document.label}
                                                href={document.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                            >
                                                <FileText size={15} />
                                                {document.label}
                                                <ExternalLink size={14} />
                                            </a>
                                        ) : (
                                            <span key={document.label} className="btn btn-outline-secondary btn-sm disabled">
                                                {document.label} indisponível
                                            </span>
                                        )
                                    ))}
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-danger d-inline-flex align-items-center gap-2"
                                    disabled={isProcessing}
                                    onClick={() => reviewRequest(request, 'reject')}
                                >
                                    <XCircle size={18} />
                                    Recusar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-dark d-inline-flex align-items-center gap-2"
                                    disabled={isProcessing}
                                    onClick={() => reviewRequest(request, 'approve')}
                                >
                                    <BadgeCheck size={18} />
                                    {isProcessing ? 'Salvando…' : 'Aprovar selo black'}
                                </button>
                            </div>
                        </div>
                    </article>
                );
            })}
        </Screen>
    );
};

export default CompanySealRequests;
